/**
 * Chat Message Component
 * Displays individual chat messages
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsDown, faThumbsUp } from '@fortawesome/free-solid-svg-icons';
import { ChatMessage as ChatMessageType } from '../types';
import { FormattedMessage } from './FormattedMessage';

interface ChatMessageProps {
  message: ChatMessageType;
  locale?: string;
  assistantName?: string;
  userLabel: string;
  assistantFallbackName: string;
  imageAltLabel: string;
  onFailedMessageClick?: () => void;
  failedMessageActionAriaLabel?: string;
  showFeedbackActions?: boolean;
  thumbsUpLabel?: string;
  thumbsDownLabel?: string;
  onThumbsUp?: () => void;
  onThumbsDown?: () => void;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  locale,
  assistantName,
  userLabel,
  assistantFallbackName,
  imageAltLabel,
  onFailedMessageClick,
  failedMessageActionAriaLabel = 'View failed message details',
  showFeedbackActions = false,
  thumbsUpLabel = 'Thumbs up',
  thumbsDownLabel = 'Thumbs down',
  onThumbsUp,
  onThumbsDown,
}) => {
  const isUser = message.role === 'user';
  const isError = message.status === 'error';
  const isStartupHtml = !isUser && message.contentFormat === 'html';
  const assistantMetaName = assistantName?.trim() || assistantFallbackName;
  const thumbsUpAriaLabel = thumbsUpLabel?.trim() || 'Thumbs up';
  const thumbsDownAriaLabel = thumbsDownLabel?.trim() || 'Thumbs down';

  const formatTime = (date: Date): string => {
    const isFrench = typeof locale === 'string' && locale.toLowerCase().startsWith('fr');
    return new Date(date).toLocaleTimeString(isFrench ? 'fr-CA' : 'en-US', {
      hour: isFrench ? '2-digit' : 'numeric',
      minute: '2-digit',
      hour12: !isFrench,
    });
  };

  return (
    <div className={`chatbot-message ${isUser ? 'chatbot-message-user' : 'chatbot-message-assistant'}`}>
      <div className="chatbot-message-content">
        {!isUser && (
          <div className="chatbot-message-avatar">
            <svg width="24" height="24" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <rect width="56" height="56" rx="28" fill="var(--chatbot-primary)"/>
              <path d="M22 34C22.8607 34 23.6241 34.5508 23.8965 35.3672L24.2656 36.4707L24.3516 36.6875C24.5826 37.18 25.0069 37.56 25.5293 37.7344L26.6328 38.1035L26.9258 38.2266C27.5784 38.567 28 39.2467 28 40C28 40.8607 27.4492 41.6241 26.6328 41.8965L25.5293 42.2656C25.0069 42.44 24.5826 42.82 24.3516 43.3125L24.2656 43.5293L23.8965 44.6328C23.6241 45.4492 22.8607 46 22 46C21.2467 46 20.567 45.5784 20.2266 44.9258L20.1035 44.6328L19.7344 43.5293C19.56 43.0069 19.18 42.5826 18.6875 42.3516L18.4707 42.2656L17.3672 41.8965C16.5508 41.6241 16 40.8607 16 40C16 39.1393 16.5508 38.3759 17.3672 38.1035L18.4707 37.7344L18.6875 37.6484C19.18 37.4174 19.56 36.9931 19.7344 36.4707L20.1035 35.3672L20.2266 35.0742C20.567 34.4216 21.2467 34 22 34ZM35 14C35.8335 14 36.5802 14.5166 36.873 15.2969L38.0605 18.4648C39.0914 21.2139 40.9614 23.5706 43.4043 25.1992L45.1094 26.3359L45.3066 26.4863C45.7437 26.8635 46 27.4147 46 28C46 28.5853 45.7437 29.1365 45.3066 29.5137L45.1094 29.6641L43.4043 30.8008C40.9614 32.4294 39.0914 34.7861 38.0605 37.5352L36.873 40.7031C36.5802 41.4834 35.8335 42 35 42C34.2706 42 33.6082 41.6042 33.2578 40.9824L33.127 40.7031L31.9395 37.5352C30.9729 34.9577 29.2692 32.726 27.0469 31.1152L26.5957 30.8008L24.8906 29.6641C24.3342 29.2931 24 28.6687 24 28C24 27.3313 24.3342 26.7069 24.8906 26.3359L26.5957 25.1992L27.0469 24.8848C29.2692 23.274 30.9729 21.0423 31.9395 18.4648L33.127 15.2969L33.2578 15.0176C33.6082 14.3958 34.2706 14 35 14ZM18 10C18.893 10 19.6785 10.5926 19.9238 11.4512L20.332 12.8809L20.4375 13.2168C21.0149 14.8659 22.2865 16.1886 23.9258 16.8262L24.7246 17.1367C25.4934 17.4357 26 18.1752 26 19C26 19.8248 25.4934 20.5643 24.7246 20.8633L23.9258 21.1738C22.2865 21.8114 21.0149 23.1341 20.4375 24.7832L20.332 25.1191L19.9238 26.5488C19.6785 27.4074 18.893 28 18 28C17.2184 28 16.52 27.5463 16.1934 26.8574L16.0762 26.5488L15.668 25.1191C15.1846 23.4275 13.9857 22.0379 12.3965 21.3105L12.0742 21.1738L11.2754 20.8633C10.5066 20.5643 10 19.8248 10 19C10 18.1752 10.5066 17.4357 11.2754 17.1367L12.0742 16.8262L12.3965 16.6895C13.9857 15.9621 15.1846 14.5725 15.668 12.8809L16.0762 11.4512L16.1934 11.1426C16.52 10.4537 17.2184 10 18 10Z" fill="white"/>
            </svg>
          </div>
        )}
        <div className="chatbot-message-bubble">
          <div
            data-message-id={message.id}
            className={`chatbot-message-text ${isError ? 'chatbot-message-error' : ''}`}
          >
            {isUser ? (
              message.content
            ) : isStartupHtml ? (
              <div
                className="chatbot-startup-html"
                dangerouslySetInnerHTML={{ __html: message.content }}
              />
            ) : (
              <FormattedMessage content={message.content} imageAltFallback={imageAltLabel} />
            )}
          </div>
        </div>
        {isUser && isError && onFailedMessageClick && (
          <button
            type="button"
            className="chatbot-message-error-trigger"
            onClick={onFailedMessageClick}
            aria-label={failedMessageActionAriaLabel}
            title={failedMessageActionAriaLabel}
          >
            !
          </button>
        )}
      </div>
      {!isUser && !isStartupHtml && !isError && showFeedbackActions && (
        <div className="chatbot-message-feedback">
          <button
            type="button"
            className="chatbot-message-feedback-btn"
            onClick={onThumbsUp}
            aria-label={thumbsUpAriaLabel}
            title={thumbsUpAriaLabel}
          >
            <FontAwesomeIcon icon={faThumbsUp} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="chatbot-message-feedback-btn"
            onClick={onThumbsDown}
            aria-label={thumbsDownAriaLabel}
            title={thumbsDownAriaLabel}
          >
            <FontAwesomeIcon icon={faThumbsDown} aria-hidden="true" />
          </button>
        </div>
      )}
      <div className={`chatbot-message-meta ${isUser ? 'chatbot-message-meta-user' : 'chatbot-message-meta-assistant'}`}>
        <span className="chatbot-message-meta-name">{isUser ? userLabel : assistantMetaName}</span>
        <span className="chatbot-message-meta-separator">,&nbsp;</span>
        <span className="chatbot-message-time">{formatTime(message.timestamp)}</span>
      </div>
    </div>
  );
};
